import type {ReactNode} from 'react';
import {useState} from 'react';

import {cn} from '@/utils/cn';

export type SubTabItem = {
  id: string;
  label: string;
  content: ReactNode;
};

type SubTabsProps = {
  tabs: SubTabItem[];
  className?: string;
  activeId?: string;
  onChange?: (id: string) => void;
  /**
   * Optional test hook prefix. When set, each tab button renders
   * `data-cy="<dataCy>-<tab id>"` so specs can switch sub-tabs by a stable
   * selector rather than their visible label.
   */
  dataCy?: string;
};

const SubTabs = ({
  tabs,
  className = '',
  activeId,
  onChange,
  dataCy,
}: SubTabsProps) => {
  const [internalActiveId, setInternalActiveId] = useState(tabs[0]?.id);
  const resolvedActiveId = activeId ?? internalActiveId;
  const activeTab = tabs.find((tab) => tab.id === resolvedActiveId) ?? tabs[0];

  const handleClick = (id: string) => {
    if (onChange) {
      onChange(id);
    } else {
      setInternalActiveId(id);
    }
  };

  return (
    <div>
      <div className={cn('pt-3', className || 'px-3')}>
        <div className="inline-flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-dark-borderMid dark:bg-dark-blueBg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-cy={dataCy ? `${dataCy}-${tab.id}` : undefined}
              onClick={() => handleClick(tab.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${
                activeTab?.id === tab.id
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-dark-background dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {activeTab?.content}
    </div>
  );
};

export default SubTabs;
